import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../lib/auth";
import { LogoMark } from "../components/Logo";
import { toast } from "sonner";
import { Mail, RotateCw, ShieldCheck } from "lucide-react";

export default function VerifyEmail() {
  const { verifyEmail, resendCode } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error("Il codice deve essere di 6 cifre"); return; }
    setLoading(true);
    try {
      const u = await verifyEmail(email, code);
      toast.success("Email verificata! Benvenuto.");
      // Se viene dall'onboarding o se è admin senza piano, vai alla scelta del piano
      if (params.get("onboarding") === "1" && u?.role === "admin") {
        nav("/onboarding/plan");
      } else {
        nav(u?.role === "employee" ? "/portal" : "/app");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Codice non valido");
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (!email) { toast.error("Inserisci l'email"); return; }
    setResending(true);
    try {
      await resendCode(email);
      toast.success("Nuovo codice inviato. Controlla la tua casella email.");
      setCooldown(60);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore reinvio");
    } finally { setResending(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center px-6 py-10 relative overflow-hidden">
      <div className="orb orb-orange" style={{width: 500, height: 500, top: -200, right: -100, opacity: 0.25}}/>
      <div className="relative w-full max-w-md card-smart p-8 md:p-10" data-testid="verify-card">
        <Link to="/" className="inline-flex mb-6" aria-label="Verixa home">
          <LogoMark size={64} pulse/>
        </Link>
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#3B82F6] mb-3">
          <ShieldCheck className="w-4 h-4"/> Verifica in due passaggi
        </div>
        <h1 className="font-display text-3xl mb-2">Conferma la tua email</h1>
        <p className="text-sm text-[#757575] mb-6">Abbiamo inviato un codice di 6 cifre a <strong>{email || "la tua email"}</strong>. Inseriscilo qui sotto per attivare l'account.</p>

        <form onSubmit={submit} className="space-y-4">
          {!params.get("email") && (
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#757575]"/>
                <Input id="email" data-testid="verify-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className="rounded-xl pl-10 h-11"/>
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="code">Codice di verifica</Label>
            <Input
              id="code"
              data-testid="verify-code"
              required
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              pattern="[0-9]{6}"
              value={code}
              onChange={(e)=>setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              className="rounded-xl mt-1 h-14 text-center font-mono text-3xl tracking-[0.5em] font-bold"
            />
            <div className="text-xs text-[#757575] mt-1">Il codice è valido per 24 ore</div>
          </div>
          <Button type="submit" disabled={loading || code.length !== 6} data-testid="verify-submit" className="w-full rounded-full h-12 bg-[#3B82F6] hover:bg-[#7C3AED] btn-glow shadow-md shadow-[#3B82F6]/25">
            {loading ? "Verifica…" : "Conferma e accedi"}
          </Button>
        </form>

        <div className="mt-6 pt-6 border-t border-[#1F2540] flex items-center justify-between">
          <span className="text-xs text-[#757575]">Non hai ricevuto l'email?</span>
          <Button type="button" variant="ghost" disabled={resending || cooldown > 0} onClick={resend} data-testid="resend-code" className="text-[#3B82F6] text-xs">
            <RotateCw className={`w-3 h-3 mr-1 ${resending ? "animate-spin" : ""}`}/>
            {cooldown > 0 ? `Riinvia tra ${cooldown}s` : "Reinvia codice"}
          </Button>
        </div>

        <p className="text-sm text-[#555] mt-6 text-center"><Link to="/login" className="text-[#3B82F6] hover:underline">← Torna al login</Link></p>
      </div>
    </div>
  );
}
